import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NumberSchemaItem, RowWithEmbeddedMetadata } from "@data-root/types";
import { SchemaItemProps, useField } from "@data-root/react";
import { inputGuard } from "../../hocs/guard";
import { View } from "@data-root/react";
import { InputNumber, Skeleton } from "antd";
import { Typography, Slider } from "antd";
import { Remarks } from "../remarks";

const { Title } = Typography;

export const NumberInputSliderPresentation: FC<
  SchemaItemProps<NumberSchemaItem>
> = ({ item }) => {
  const { loaded, displayName, editable, show, value, setValue, remarks } =
    useField(item);

  const handleSetValue = useCallback(
    (value: number | null) => {
      if (value === null) return setValue(null, "");
      setValue(value, value?.toString() || "");
    },
    [setValue]
  );

  if (!show) return null;

  return (
    <View style={{ width: "100%" }}>
      <Title level={5}>{displayName}</Title>

      {!loaded ? (
        <Skeleton.Input block active />
      ) : (
        <Slider
          disabled={!editable}
          min={item.min}
          max={item.max}
          step={1}
          value={(value as number) ?? item.min}
          onChange={handleSetValue}
        />
      )}

      <Remarks texts={remarks} />
    </View>
  );
};

export const NumberInputDefaultPresentation: FC<
  SchemaItemProps<NumberSchemaItem>
> = ({ item }) => {
  const {
    loaded,
    displayName,
    placeholder,
    editable,
    show,
    defaultValue,
    subscribe,
    setValue: _setValue,
    remarks,
  } = useField<number>(item);

  const [value, setValue] = useState<number | null>(defaultValue);

  const handleSetValue = useCallback(
    (value: number | null) => {
      if (value === null) {
        setValue(null);
        _setValue(null, "");
        return;
      }

      setValue(value);
      _setValue(value, value?.toString() || "");
    },

    [_setValue]
  );

  useEffect(() => {
    const callback = (
      next: RowWithEmbeddedMetadata,
      options?: {
        init?: boolean;
        evaluation?: boolean;
      }
    ) => {
      if (options?.init) {
        setValue(next[item.id] as number);
        return;
      }
      if (!options?.evaluation) return;

      setValue(next[item.id] as number);
    };

    const unsubscribe = subscribe(callback);

    return () => {
      unsubscribe();
    };
  }, [item.id, subscribe]);

  if (!show) return null;

  return (
    <View style={{ width: "100%" }}>
      <Title level={5}>{displayName}</Title>

      {!loaded ? (
        <Skeleton.Input block active />
      ) : (
        <InputNumber
          style={{ width: "100%" }}
          min={item.min}
          max={item.max}
          readOnly={!editable}
          disabled={!editable}
          placeholder={placeholder}
          value={value}
          onChange={handleSetValue}
          precision={0}
          inputMode="decimal"
        />
      )}

      <Remarks texts={remarks} />
    </View>
  );
};

export const NumberInput: FC<SchemaItemProps<NumberSchemaItem>> = inputGuard(
  (props) => {
    const { item } = props;
    const { presentation: _presentation } = item;

    const presentation = useMemo(
      () => _presentation || "Default",
      [_presentation]
    );

    switch (presentation) {
      case "Slider":
        return <NumberInputSliderPresentation {...props} />;
      case "Default":
      default:
        return <NumberInputDefaultPresentation {...props} />;
    }
  }
);
